import { Controller } from "@hotwired/stimulus"

// Tooltip V2: mini-resumen lazy en un trigger (link/botón). Carga perezosa: al
// abrir, si el <turbo-frame> no tiene src se le asigna (data-src-value) → Turbo
// pide el resumen la 1ra vez, no al cargar la página.
//
// Modo estático: si el trigger no tiene src-value (ButtonComponent icon-only),
// la burbuja ya viene en el DOM y load() no hace nada. En touch, el 1er tap
// muestra el tooltip y frena; el 2do tap deja pasar el click — el botón
// ejecuta su acción (delete/unassign), no se queda preso en el toggle.
//
// Trigger:
//   - Desktop (hover: hover): mouseenter/focus abre, mouseleave/blur/Esc cierra.
//   - Touch (hover: none): tap abre/cierra (toggle) y frena la navegación del
//     link; el resumen cargado puede traer su propio link al detalle.
//
// Dirección: data-direction-value (top/bottom/left/right). position() calcula
// coordenadas DE VIEWPORT (la burbuja es position: fixed: no la recortan los
// overflow de tablas/cards) y hace AUTO-FLIP al lado opuesto si la burbuja
// desborda. Como fixed no scrollea con el trigger, cualquier scroll la cierra.
//
// TAB.T3 (Detalle 14-09 "tooltips prácticamente inmediatos"): retardo de
// apertura configurable POR TRIGGER con data-v2--tooltip-delay-value (ms).
// Default 0 = inmediato — no hay delay global que desactivar; el valor queda
// expuesto para quien necesite una espera (foco/tap abren siempre directo:
// son interacciones deliberadas, no hover).
const GAP = 8       // separación burbuja↔trigger (px)
const MARGIN = 8    // margen al borde del viewport (px)

export default class extends Controller {
  static values = {
    direction: { type: String, default: "top" },
    src: String,
    delay: { type: Number, default: 0 }
  }
  static targets = ["bubble", "frame"]

  connect() {
    this.open = false
    this.openTimer = null
    // hover:hover ⇒ dispositivo con mouse; si no, sólo tap.
    this.canHover = window.matchMedia("(hover: hover)").matches
    this.onDocClick = this.onDocClick.bind(this)
    this.onKey = this.onKey.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onScroll = this.onScroll.bind(this)
    document.addEventListener("click", this.onDocClick)
    document.addEventListener("keydown", this.onKey)
    window.addEventListener("resize", this.onResize)
    // capture: los scrolls de contenedores (.table-responsive, cards) no
    // burbujean a window.
    document.addEventListener("scroll", this.onScroll, true)
  }

  disconnect() {
    this.cancelScheduledOpen()
    document.removeEventListener("click", this.onDocClick)
    document.removeEventListener("keydown", this.onKey)
    window.removeEventListener("resize", this.onResize)
    document.removeEventListener("scroll", this.onScroll, true)
  }

  // ── Acciones ──
  show()  { this.canHover && this.scheduleOpen() }  // mouseenter (desktop)
  hide()  { this.canHover && this.cancelAndClose() } // mouseleave (desktop)
  focus() { this.openTooltip() }                     // teclado: siempre
  blur()  { this.closeTooltip() }

  // Hover con delay opcional: 0 (default) abre al instante; >0 espera esos ms
  // y cancela si el cursor se va antes.
  scheduleOpen() {
    if (this.open) return
    this.cancelScheduledOpen()
    if (this.delayValue > 0) {
      this.openTimer = setTimeout(() => this.openTooltip(), this.delayValue)
    } else {
      this.openTooltip()
    }
  }

  cancelScheduledOpen() {
    if (this.openTimer) {
      clearTimeout(this.openTimer)
      this.openTimer = null
    }
  }

  cancelAndClose() {
    this.cancelScheduledOpen()
    this.closeTooltip()
  }

  // Tap (touch): toggle. En desktop el click no hace nada (gana el hover).
  // Modo estático (sin src): 1er tap muestra y frena, 2do tap cierra y DEJA
  // PASAR el click — el botón ejecuta su acción.
  toggle(event) {
    if (this.canHover) return
    if (!this.hasSrcValue) {
      if (!this.open) {
        event.preventDefault()
        this.openTooltip()
      } else {
        this.closeTooltip()
      }
      return
    }
    event.preventDefault()
    this.open ? this.closeTooltip() : this.openTooltip()
  }

  // ── Estado ──
  openTooltip() {
    if (this.open) return
    this.open = true
    this.load()
    this.bubbleTarget.classList.add("is-open")
    this.bubbleTarget.setAttribute("aria-hidden", "false")
    this.element.setAttribute("aria-expanded", "true")
    requestAnimationFrame(() => this.position())
  }

  closeTooltip() {
    if (!this.open) return
    this.open = false
    this.bubbleTarget.classList.remove("is-open")
    this.bubbleTarget.setAttribute("aria-hidden", "true")
    this.element.setAttribute("aria-expanded", "false")
  }

  // Carga perezosa: setea src del frame la 1ra vez. Pasa el id del frame al
  // endpoint (tooltip_frame_id) para que la respuesta venga envuelta en un
  // <turbo-frame> con el MISMO id — sino Turbo muestra "Content missing".
  // Modo estático (ButtonComponent icon-only): no hay frame target — no-op.
  load() {
    if (!this.hasFrameTarget) return
    const frame = this.frameTarget
    if (this.srcValue && !frame.getAttribute("src")) {
      const url = new URL(this.srcValue, window.location.origin)
      url.searchParams.set("tooltip_frame_id", frame.id)
      frame.src = url.toString()
    }
  }

  // Posiciona la burbuja (coordenadas de viewport: es position: fixed) y hace
  // auto-flip si desborda el viewport.
  position() {
    const bubble = this.bubbleTarget
    bubble.style.top = ""
    bubble.style.left = ""
    const dir = this.resolveDirection(bubble)
    bubble.dataset.placement = dir
    bubble.classList.remove("tp-tooltip__bubble--top", "tp-tooltip__bubble--bottom",
                            "tp-tooltip__bubble--left", "tp-tooltip__bubble--right")
    bubble.classList.add(`tp-tooltip__bubble--${dir}`)

    const trig = this.element.getBoundingClientRect()
    const box = bubble.getBoundingClientRect()
    let top, left

    switch (dir) {
      case "top":
        top = trig.top - box.height - GAP
        left = trig.left + (trig.width / 2) - (box.width / 2)
        break
      case "bottom":
        top = trig.bottom + GAP
        left = trig.left + (trig.width / 2) - (box.width / 2)
        break
      case "left":
        left = trig.left - box.width - GAP
        top = trig.top + (trig.height / 2) - (box.height / 2)
        break
      case "right":
        left = trig.right + GAP
        top = trig.top + (trig.height / 2) - (box.height / 2)
        break
    }

    // Clamp en el viewport: si desborda, desplazar dentro del margen.
    if (left < MARGIN) left = MARGIN
    else if (left + box.width > window.innerWidth - MARGIN) left = window.innerWidth - MARGIN - box.width
    if (top < MARGIN) top = MARGIN
    else if (top + box.height > window.innerHeight - MARGIN) top = window.innerHeight - MARGIN - box.height

    bubble.style.top = `${Math.round(top)}px`
    bubble.style.left = `${Math.round(left)}px`
  }

  // Dirección final con auto-flip si la burbuja no cabe en el lado pedido.
  resolveDirection(bubble) {
    const dir = this.directionValue || "top"
    const trig = this.element.getBoundingClientRect()
    const box = bubble.getBoundingClientRect()
    const space = {
      top: trig.top,
      bottom: window.innerHeight - trig.bottom,
      left: trig.left,
      right: window.innerWidth - trig.right,
    }
    const fits = {
      top: space.top >= box.height + GAP,
      bottom: space.bottom >= box.height + GAP,
      left: space.left >= box.width + GAP,
      right: space.right >= box.width + GAP,
    }
    if (fits[dir]) return dir
    const flip = { top: "bottom", bottom: "top", left: "right", right: "left" }[dir]
    if (flip && fits[flip]) return flip
    // Si ninguno cabe perfecto, quedarse con el lado con más espacio.
    return Object.entries(space).sort((a, b) => b[1] - a[1])[0][0]
  }

  // ── Handlers globales ──
  onDocClick(event) {
    if (!this.open) return
    if (this.element.contains(event.target)) return
    this.closeTooltip()
  }

  onKey(event) {
    if (event.key === "Escape") this.closeTooltip()
  }

  onResize() {
    if (this.open) this.position()
  }

  // La burbuja es fixed: no acompaña al trigger al scrollear. Cerrar es más
  // simple (y más honesto) que perseguir la posición.
  onScroll() {
    if (this.open) this.closeTooltip()
  }
}
